import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Linkedin, Github, Twitter, Dribbble, Mail, Phone, MapPin, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { company, services, nav } from "@/data/site";
import { subscribeNewsletter } from "@/lib/api";
import { Logo } from "@/components/layout/Navbar";

export default function Footer() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubscribe = async (e) => {
    e.preventDefault();
    if (!email || !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) {
      toast.error("Please enter a valid email address.");
      return;
    }
    setLoading(true);
    try {
      const res = await subscribeNewsletter(email);
      toast.success(res.message || "Subscribed!");
      setEmail("");
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <footer className="dark bg-[#020617] text-slate-300 grain" data-testid="footer">
      {/* Big CTA */}
      <div className="border-b border-white/10">
        <div className="mx-auto max-w-7xl px-5 py-20 sm:px-8 sm:py-28">
          <p className="mb-4 text-xs font-bold uppercase tracking-[0.25em] text-primary">Let&apos;s build together</p>
          <Link to="/contact" className="group block" data-testid="footer-cta">
            <h2 className="font-display text-[13vw] font-medium leading-[0.95] tracking-tight text-white sm:text-[9vw] lg:text-[7vw]">
              Let&apos;s Talk
              <ArrowUpRight className="ml-2 inline-block h-[8vw] w-[8vw] max-h-24 max-w-24 text-primary transition-transform duration-500 group-hover:translate-x-3 group-hover:-translate-y-2 sm:h-[6vw] sm:w-[6vw]" />
            </h2>
          </Link>
          <p className="mt-6 max-w-xl text-lg text-slate-400">
            Have a project in mind or need a technology partner you can trust? We reply within one business day.
          </p>
        </div>
      </div>

      {/* Links */}
      <div className="mx-auto max-w-7xl px-5 py-16 sm:px-8">
        <div className="grid gap-12 lg:grid-cols-[1.4fr_1fr_1fr_1.3fr]">
          <div>
            <Logo light />
            <p className="mt-5 font-display text-base text-white/90">{company.tagline}.</p>
            <p className="mt-3 max-w-xs text-sm leading-relaxed text-slate-400">{company.description}</p>
            <div className="mt-6 flex gap-3">
              {[
                { icon: Linkedin, href: company.social.linkedin, label: "LinkedIn" },
                { icon: Github, href: company.social.github, label: "GitHub" },
                { icon: Twitter, href: company.social.twitter, label: "Twitter" },
                { icon: Dribbble, href: company.social.dribbble, label: "Dribbble" },
              ].map((s) => (
                <a
                  key={s.label}
                  href={s.href}
                  target="_blank"
                  rel="noreferrer"
                  aria-label={s.label}
                  data-testid={`social-${s.label.toLowerCase()}`}
                  className="grid h-10 w-10 place-items-center rounded-full border border-white/10 text-slate-400 transition-colors hover:border-primary hover:bg-primary hover:text-white"
                >
                  <s.icon className="h-4 w-4" />
                </a>
              ))}
            </div>
          </div>

          <div>
            <h4 className="font-display text-sm font-semibold uppercase tracking-wider text-white">Services</h4>
            <ul className="mt-5 space-y-3 text-sm">
              {services.slice(0, 6).map((s) => (
                <li key={s.slug}>
                  <Link to={`/services#${s.slug}`} className="text-slate-400 transition-colors hover:text-white">
                    {s.title}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="font-display text-sm font-semibold uppercase tracking-wider text-white">Company</h4>
            <ul className="mt-5 space-y-3 text-sm">
              <li><Link to="/about" className="text-slate-400 transition-colors hover:text-white">About Us</Link></li>
              {nav.filter((n) => ["Industries", "Technologies", "Portfolio", "Case Studies", "Careers", "Blog"].includes(n.label)).map((n) => (
                <li key={n.to}><Link to={n.to} className="text-slate-400 transition-colors hover:text-white">{n.label}</Link></li>
              ))}
              <li><Link to="/contact" className="text-slate-400 transition-colors hover:text-white">Contact</Link></li>
            </ul>
          </div>

          <div>
            <h4 className="font-display text-sm font-semibold uppercase tracking-wider text-white">Stay in the loop</h4>
            <p className="mt-5 text-sm text-slate-400">Insights on AI, cloud and engineering — no spam.</p>
            <form onSubmit={handleSubscribe} className="mt-4 flex gap-2" data-testid="newsletter-form">
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@company.com"
                data-testid="newsletter-email"
                className="min-w-0 flex-1 rounded-full border border-white/10 bg-white/5 px-4 py-2.5 text-sm text-white placeholder:text-slate-500 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/40"
              />
              <button
                type="submit"
                disabled={loading}
                data-testid="newsletter-submit"
                className="grid h-10 w-11 shrink-0 place-items-center rounded-full bg-primary text-white transition-opacity hover:opacity-90 disabled:opacity-60"
              >
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowUpRight className="h-4 w-4" />}
              </button>
            </form>

            <ul className="mt-7 space-y-3 text-sm">
              <li className="flex items-center gap-3 text-slate-400"><Mail className="h-4 w-4 text-primary" /> {company.email}</li>
              <li className="flex items-center gap-3 text-slate-400"><Phone className="h-4 w-4 text-primary" /> {company.phone}</li>
              <li className="flex items-start gap-3 text-slate-400"><MapPin className="mt-0.5 h-4 w-4 shrink-0 text-primary" /> {company.address}</li>
            </ul>
          </div>
        </div>

        <div className="mt-14 flex flex-col items-center justify-between gap-4 border-t border-white/10 pt-8 text-xs text-slate-500 sm:flex-row">
          <p>© {new Date().getFullYear()} {company.name}. All rights reserved.</p>
          <div className="flex gap-6">
            <Link to="/privacy" className="hover:text-white">Privacy Policy</Link>
            <Link to="/terms" className="hover:text-white">Terms of Service</Link>
            <Link to="/admin" className="hover:text-white" data-testid="footer-admin-link">Admin</Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
